import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, FileText, Package, Clock, ArrowLeft, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUnifiedSearch } from '@/hooks/useUnifiedSearch';
import type { SearchResult } from '@/services/searchService';

/**
 * Comprehensive search results page with filtering and categorization
 *
 * Features:
 * - Tabbed interface for different content types
 * - Advanced filtering options
 * - Search result snippets with highlighting
 * - Performance metrics display
 * - Responsive design for mobile and desktop
 */
export function SearchResultsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState<'all' | 'products' | 'documentation'>('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'name' | 'date'>('relevance');

  // Get search query from URL
  const urlQuery = searchParams.get('q') || '';

  // Search with current parameters
  const {
    data: searchResults,
    isLoading,
    error,
    hasSearched,
    isEnabled,
  } = useUnifiedSearch(urlQuery, {
    enabled: Boolean(urlQuery),
    limit: 20, // More results for the dedicated search page
  });

  // Update local state when URL changes
  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    setQuery(urlQuery);
  }, [searchParams]);

  // Handle new search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    }
  };

  // Handle tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as 'all' | 'products' | 'documentation');
  };

  // Filter results based on active tab
  const getFilteredResults = (): SearchResult[] => {
    if (!searchResults) return [];

    switch (activeTab) {
      case 'products':
        return searchResults.products;
      case 'documentation':
        return searchResults.documentation;
      default:
        return [...searchResults.products, ...searchResults.documentation];
    }
  };

  // Sort results
  const getSortedResults = (results: SearchResult[]): SearchResult[] => {
    switch (sortBy) {
      case 'name':
        return [...results].sort((a, b) => a.title.localeCompare(b.title));
      case 'date':
        return [...results].sort((a, b) => {
          const aDate = a.metadata.created_at || a.metadata.indexed_at;
          const bDate = b.metadata.created_at || b.metadata.indexed_at;
          return new Date(bDate || 0).getTime() - new Date(aDate || 0).getTime();
        });
      default: // relevance
        return [...results].sort((a, b) => b.score - a.score);
    }
  };

  const filteredResults = getFilteredResults();
  const sortedResults = getSortedResults(filteredResults);

  // Result counts by type
  const productCount = searchResults?.product_count || 0;
  const documentationCount = searchResults?.documentation_count || 0;
  const totalCount = searchResults?.total_results || 0;

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
  };

  // Render individual search result
  const renderSearchResult = (result: SearchResult) => {
    const Icon = result.type === 'product' ? Package : FileText;
    const typeLabel = result.type === 'product' ? t('badges.product') : t('badges.documentation');
    const metadata = result.metadata;

    return (
      <Card
        key={`${result.type}-${result.id}`}
        className="cursor-pointer transition-colors hover:bg-muted/50"
        onClick={() => handleResultClick(result)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <Icon className="size-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <CardTitle className="truncate text-base">{result.title}</CardTitle>
                <Badge variant="secondary" className="text-xs">
                  {typeLabel}
                </Badge>
                {result.score > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {Math.round(result.score * 100)}% {t('metadata.matchScore')}
                  </Badge>
                )}
              </div>
              {result.snippet && (
                <p className="line-clamp-2 text-sm text-muted-foreground">{result.snippet}</p>
              )}
            </div>
          </div>
        </CardHeader>
        {(metadata.sku || metadata.brand || metadata.language) && (
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {metadata.sku && (
                <span className="flex items-center gap-1">
                  <strong>{t('metadata.sku')}:</strong> {metadata.sku}
                </span>
              )}
              {metadata.brand && (
                <span className="flex items-center gap-1">
                  <strong>{t('metadata.brand')}:</strong> {metadata.brand}
                </span>
              )}
              {metadata.language && (
                <span className="flex items-center gap-1">
                  <strong>{t('metadata.language')}:</strong> {metadata.language.toUpperCase()}
                </span>
              )}
              {metadata.word_count && (
                <span className="flex items-center gap-1">
                  <strong>{t('metadata.wordCount')}:</strong> {metadata.word_count}
                </span>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="size-4" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{t('searchResults.title')}</h1>
            {hasSearched && (
              <p className="text-muted-foreground">
                {isLoading
                  ? t('searchBar.loading')
                  : `${totalCount} ${t('searchResults.resultsFound')}`}
                {searchResults?.search_time_ms && (
                  <span className="ml-2 text-xs">({searchResults.search_time_ms}ms)</span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t('searchBar.placeholder')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="pl-9 pr-20"
          />
          <Button
            type="submit"
            size="sm"
            className="absolute right-2 top-1/2 -translate-y-1/2"
            disabled={!query.trim()}
          >
            {t('actions.search')}
          </Button>
        </form>
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="mb-2 text-destructive">Search Error</p>
              <p className="text-sm text-muted-foreground">{error.message}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                className="mt-3"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {(isEnabled || hasSearched) && !error && (
        <>
          {/* Controls */}
          <div className="mb-4 flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="all">
                  {t('searchTypes.all')} ({totalCount})
                </TabsTrigger>
                <TabsTrigger value="products">
                  {t('searchTypes.products')} ({productCount})
                </TabsTrigger>
                <TabsTrigger value="documentation">
                  {t('searchTypes.documentation')} ({documentationCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-2">
              <Filter className="size-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={value => setSortBy(value as any)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">{t('searchResults.relevance')}</SelectItem>
                  <SelectItem value="name">{t('searchResults.alphabetical')}</SelectItem>
                  <SelectItem value="date">{t('searchResults.dateUpdated')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="py-8 text-center">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Clock className="size-4 animate-spin" />
                {t('searchBar.loading')}
              </div>
            </div>
          )}

          {/* Results List */}
          {!isLoading && (
            <>
              {sortedResults.length > 0 ? (
                <div className="space-y-4">{sortedResults.map(renderSearchResult)}</div>
              ) : hasSearched ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="py-8 text-center">
                      <Search className="mx-auto mb-4 size-12 text-muted-foreground" />
                      <h3 className="mb-2 text-lg font-medium">
                        {t('searchResults.noResultsFound')}
                      </h3>
                      <p className="mb-4 text-muted-foreground">
                        {t('searchResults.noResultsMessage')}
                      </p>
                      <div className="flex justify-center gap-2">
                        <Button variant="outline" onClick={() => navigate('/app/products')}>
                          Browse Products
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/app/documentation')}>
                          View Documentation
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <div className="py-8 text-center">
                      <Search className="mx-auto mb-4 size-12 text-muted-foreground" />
                      <h3 className="mb-2 text-lg font-medium">Start your search</h3>
                      <p className="text-muted-foreground">
                        Enter a search term above to find products and documentation.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
